import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { ChevronRight, X, Sparkles } from 'lucide-react';

function getRect(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  return el.getBoundingClientRect();
}

export function TourOverlay() {
  const { activeTour, activeStep, nextStep, skipTour } = useTour();
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);

  const step = activeTour?.steps?.[activeStep];

  useEffect(() => {
    if (!step?.target) { setRect(null); return; }
    const update = () => {
      const el = document.querySelector(step.target);
      if (!el) { setRect(null); return; }
      // Scroll element into view if needed
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Wait a tick for scroll to settle, then measure
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        setRect(r);
        const pad = 12;
        const tWidth = 320;
        const tHeight = 180;
        let top = r.bottom + pad;
        let left = r.left + r.width / 2 - tWidth / 2;
        if (top + tHeight > window.innerHeight) top = r.top - tHeight - pad;
        if (top < 10) top = 10;
        if (left < 12) left = 12;
        if (left + tWidth > window.innerWidth - 12) left = window.innerWidth - 12 - tWidth;
        setTooltipPos({ top, left });
      });
    };
    // Delay initial to allow page rendering
    const timer = setTimeout(update, 300);
    const timer2 = setTimeout(update, 800);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step]);

  if (!activeTour || !step) return null;

  const totalSteps = activeTour.steps.length;
  const isLast = activeStep === totalSteps - 1;
  const padding = 6;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="tour-overlay">
      {/* Dark overlay with cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - padding}
                y={rect.top - padding}
                width={rect.width + padding * 2}
                height={rect.height + padding * 2}
                rx="4"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(15,23,42,0.55)" mask="url(#tour-mask)" style={{ pointerEvents: 'all' }} onClick={skipTour} />
      </svg>

      {/* Spotlight ring around target */}
      {rect && (
        <div
          className="absolute border-2 border-[#0047AB] rounded pointer-events-none animate-pulse"
          style={{
            left: rect.left - padding,
            top: rect.top - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
            boxShadow: '0 0 0 4px rgba(0,71,171,0.15)',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="absolute z-[10000] w-[320px] bg-white border border-slate-200 rounded-lg shadow-2xl overflow-hidden animate-slideIn"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
        data-testid="tour-tooltip"
      >
        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-[#0047AB] to-[#0891B2]" />
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-[#0047AB]" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#0047AB] font-semibold">{step.title}</span>
            </div>
            <button onClick={skipTour} className="text-slate-400 hover:text-slate-600 transition-colors p-0.5" data-testid="tour-skip-x">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-t border-slate-100">
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={`step-dot-${i}`}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === activeStep ? 'bg-[#0047AB] w-4' : i < activeStep ? 'bg-[#0047AB]/40' : 'bg-slate-300'}`}
              />
            ))}
            <span className="text-[10px] text-slate-400 ml-1">{activeStep + 1}/{totalSteps}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={skipTour} className="text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="tour-skip-btn">
              Skip
            </button>
            <Button
              onClick={nextStep}
              size="sm"
              className="h-7 text-xs bg-[#0047AB] hover:bg-[#003580] text-white rounded-md px-3"
              data-testid="tour-next-btn"
            >
              {isLast ? 'Done' : 'Next'} {!isLast && <ChevronRight className="w-3 h-3 ml-0.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
